import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/config/react-query";
import { parseError } from "@/lib/parse-error";
import { toast } from "@/lib/use-toast";
import { GFEVisitType } from "@/types/enums/gfe-visit-type";
import { FavoriteTreatmentsResponse, GFECreateAppointmentRequest, GFECreateInPersonRequest, GFECreateRequest, GFEDetail, GFESyncPreviewResponse, GFESyncRequest, GFESyncResponse, TreatmentsResponse } from "@/types/spakinect";
import { requestData } from "@/api/api-config";

/**
 * Get all available treatments from Spakinect
 */
async function getAvailableTreatments(): Promise<TreatmentsResponse | null> {
  return await requestData<TreatmentsResponse>("spakinect/treatments", "get", undefined, false);
}

/**
 * Get all GFE records for a patient
 */
async function getPatientGFEs(patientId: string): Promise<GFEDetail[] | null> {
  return await requestData<GFEDetail[]>(`spakinect/gfe/patient/${patientId}`, "get", undefined, false);
}

/**
 * Base function to create a Good Faith Exam request
 */
async function createGFERequest(data: GFECreateRequest): Promise<GFEDetail | null> {
  return await requestData<GFEDetail>("spakinect/gfe", "post", {
    json: data
  }, false);
}

/**
 * Create an AT_HOME (at home) Good Faith Exam request
 */
async function createGFEAppointment(data: GFECreateAppointmentRequest): Promise<GFEDetail | null> {
  return createGFERequest({
    ...data,
    visitType: GFEVisitType.AT_HOME
  });
}

/**
 * Create an ON_DEMAND (on demand) Good Faith Exam request
 */
async function createGFEInPerson(data: GFECreateInPersonRequest): Promise<GFEDetail | null> {
  return createGFERequest({
    ...data,
    visitType: GFEVisitType.ON_DEMAND
  });
}

/**
 * Cancel a Good Faith Exam appointment
 */
async function cancelGFE(gfeId: string): Promise<GFEDetail | null> {
  return await requestData<GFEDetail>(`spakinect/gfe/${gfeId}/cancel`, "patch", undefined, false);
}

/**
 * Get favorite treatments for this tenant
 */
async function getFavoriteTreatments(): Promise<FavoriteTreatmentsResponse | null> {
  return await requestData<FavoriteTreatmentsResponse>("spakinect/treatments/favorites", "get", undefined, false);
}

/**
 * Set favorite treatments for this tenant (replaces all)
 */
async function setFavoriteTreatments(treatmentNames: string[]): Promise<FavoriteTreatmentsResponse | null> {
  return await requestData<FavoriteTreatmentsResponse>("spakinect/treatments/favorites", "put", {
    json: {
      treatmentNames
    }
  }, false);
}
export const spakinectQueryKeys = {
  all: ["spakinect"] as const,
  treatments: () => [...spakinectQueryKeys.all, "treatments"] as const,
  favoriteTreatments: () => [...spakinectQueryKeys.all, "favorite-treatments"] as const,
  patientGfes: (patientId: string) => [...spakinectQueryKeys.all, "gfe", "patient", patientId] as const
};
export const spakinectQueryOptions = {
  treatments: () => ({
    queryKey: spakinectQueryKeys.treatments(),
    queryFn: getAvailableTreatments,
    select: (data: TreatmentsResponse | null) => ({
      treatments: data?.treatments ?? [],
      treatmentOptions: data?.treatments?.map(treatment => ({
        value: treatment.name,
        label: treatment.name
      })) ?? []
    })
  }),
  favoriteTreatments: () => ({
    queryKey: spakinectQueryKeys.favoriteTreatments(),
    queryFn: getFavoriteTreatments,
    select: (data: FavoriteTreatmentsResponse | null) => data?.favorites ?? []
  }),
  patientGfes: (patientId: string) => ({
    queryKey: spakinectQueryKeys.patientGfes(patientId),
    queryFn: () => getPatientGFEs(patientId),
    select: (data: GFEDetail[] | null) => data ?? []
  })
};
const SPAKINECT_EMR_LOCATION_AUTH_MESSAGE = "Spakinect is not authorized for this location.";

// Shared handlers for GFE creation mutations
const handleGFECreateSuccess = (patientId: string) => {
  // Invalidate patient GFEs query
  queryClient.invalidateQueries({
    queryKey: spakinectQueryKeys.patientGfes(patientId)
  });
};
const createGFECreateErrorHandler = (defaultMessage: string) => {
  return async (error: any) => {
    const errorMessage = await parseError(error, defaultMessage);
    toast({
      title: errorMessage.startsWith(SPAKINECT_EMR_LOCATION_AUTH_MESSAGE) ? "Spakinect EMR setup needed" : "Error",
      description: errorMessage,
      variant: "error"
    });
  };
};
export const useCreateGFEAppointmentMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: createGFEAppointment,
      onSuccess: _temp,
      onError: createGFECreateErrorHandler("Failed to create Good Faith Exam appointment. Please try again.")
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useCreateGFEInPersonMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: createGFEInPerson,
      onSuccess: _temp2,
      onError: createGFECreateErrorHandler("Failed to create in-person Good Faith Exam. Please try again.")
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useCancelGFEMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp3,
      onSuccess: _temp4,
      onError: _temp5
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};

// ============================================================================
// Sync GFEs Functions and Hooks
// ============================================================================

/**
 * Get a preview of GFEs that can be synced from Spakinect for a location
 */
async function getSyncPreview(locationId: string): Promise<GFESyncPreviewResponse | null> {
  return await requestData<GFESyncPreviewResponse>(`spakinect/gfe/location/${locationId}/sync-preview`, "get", undefined, false);
}

/**
 * Import GFEs from Spakinect for matched patients
 */
async function syncGFEs(locationId: string, request: GFESyncRequest): Promise<GFESyncResponse | null> {
  return await requestData<GFESyncResponse>(`spakinect/gfe/location/${locationId}/sync`, "post", {
    json: request
  }, false);
}
export const spakinectSyncQueryKeys = {
  syncPreview: (locationId: string) => [...spakinectQueryKeys.all, "sync-preview", locationId] as const
};
export const useSyncPreviewQuery = (locationId, t0) => {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== locationId) {
    t1 = spakinectSyncQueryKeys.syncPreview(locationId);
    $[0] = locationId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== locationId) {
    t2 = () => getSyncPreview(locationId);
    $[2] = locationId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = enabled && !!locationId;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      select: _temp6
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
};
export const useSetFavoriteTreatmentsMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: setFavoriteTreatments,
      onSuccess: _temp7,
      onError: _temp8
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useSyncGFEsMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp9,
      onSuccess: _temp10,
      onError: _temp11
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
function _temp(_data, variables) {
  handleGFECreateSuccess(variables.patientId);
}
function _temp2(_data, variables) {
  handleGFECreateSuccess(variables.patientId);
}
function _temp3(t0) {
  const {
    gfeId
  } = t0;
  return cancelGFE(gfeId);
}
function _temp4(_data, variables) {
  queryClient.invalidateQueries({
    queryKey: spakinectQueryKeys.patientGfes(variables.patientId)
  });
}
async function _temp5(error) {
  const errorMessage = await parseError(error, "Failed to cancel Good Faith Exam. Please try again.");
  toast({
    title: "Error",
    description: errorMessage,
    variant: "error"
  });
}
function _temp6(data) {
  return data ?? {
    alreadyImported: [],
    matched: [],
    unmatched: []
  };
}
function _temp7() {
  queryClient.invalidateQueries({
    queryKey: spakinectQueryKeys.favoriteTreatments()
  });
}
async function _temp8(error) {
  const errorMessage = await parseError(error, "Failed to save favorite treatments. Please try again.");
  toast({
    title: "Error",
    description: errorMessage,
    variant: "error"
  });
}
function _temp9(t0) {
  const {
    locationId,
    request
  } = t0;
  return syncGFEs(locationId, request);
}
function _temp10(_data, variables) {
  queryClient.invalidateQueries({
    queryKey: spakinectSyncQueryKeys.syncPreview(variables.locationId)
  });
  queryClient.invalidateQueries({
    queryKey: spakinectQueryKeys.all
  });
}
async function _temp11(error) {
  const errorMessage = await parseError(error, "Failed to sync Good Faith Exams. Please try again.");
  toast({
    title: "Error",
    description: errorMessage,
    variant: "error"
  });
}